import { JournalEntrySourceType } from "@common/schemas/domains/accounting/journalEntry";
import {
    JournalEntryNew,
    JournalEntrySource,
} from "@common/types/domains/accounting/journalEntry";
import { ManualJournalEntrySource } from "@common/types/domains/accounting/manualJournalEntry";
import {
    CASH_ITEM_TRANSACTION_TYPE_LABELS,
    TransactionType,
} from "@common/types/transaction";
import {
    Account,
    AccountGroup,
    AccountGroupHierarchyNode,
    AccountRole,
} from "@common/types/domains/accounting/account";
import {
    defaultSolePropEquityChartOfAccounts,
    defaultSCorpEquityChartOfAccounts,
    defaultPartnershipEquityChartOfAccounts,
    SolePropEquityAccountCodes,
    SCorpAccountCodes,
    PartnershipAccountCodes,
} from "@common/constants/domains/accounting/chartOfAccounts";
import { AccountType } from "@common/constants/domains/accounting/accounts";
import {
    compareAccountsByLeafGroup,
    getLeafGroupLabel,
    getPredefinedGroupTierLabels,
    resolveLeafType,
} from "@common/constants/domains/accounting/accountGroups";
import { InvoiceLineItemKind } from "@common/types/invoice";
import { getShortUuidWithPrefix } from "./uuid";

export const JournalEntrySourceTypeLabels: Record<
    JournalEntrySourceType,
    string
> = {
    [JournalEntrySourceType.Transaction]: "Transactions",
    [JournalEntrySourceType.ManualJournalEntry]: "Manual Journal",
    [JournalEntrySourceType.OpeningBalance]: "Opening Balance",
    [JournalEntrySourceType.Loan]: "Loan",
    [JournalEntrySourceType.Bill]: "Bill",
    [JournalEntrySourceType.Invoice]: "Invoice",
    [JournalEntrySourceType.Schedule]: "Schedule",
};

export const ManualJournalEntrySourceLabels: Record<
    ManualJournalEntrySource,
    string
> = {
    [ManualJournalEntrySource.USER]: "Manual Journal",
    // Entries authored through the Platform API are ordinary manual journals
    // to everyone reading the books; the source only records where they came
    // from, so it carries the same label as a user-authored entry.
    [ManualJournalEntrySource.API]: "Manual Journal",
    [ManualJournalEntrySource.RECURRING]: "Recurring Journal",
    [ManualJournalEntrySource.QUICKBOOKS]: "QuickBooks Online",
    [ManualJournalEntrySource.QUICKBOOKS_DESKTOP]: "QuickBooks Desktop",
    [ManualJournalEntrySource.XERO]: "Xero",
    [ManualJournalEntrySource.CONSOLIDATION_ELIMINATING]: "Eliminating Journal",
};

const ManualJournalEntryOriginLabels: Record<string, string> = {
    ...ManualJournalEntrySourceLabels,
};

const TransactionOriginLabels: Record<string, string> = {
    ...CASH_ITEM_TRANSACTION_TYPE_LABELS,
};

export function getJournalEntryDisplaySource(
    source: JournalEntrySource,
): string {
    if (
        source.type === JournalEntrySourceType.ManualJournalEntry &&
        source.origin
    ) {
        return (
            ManualJournalEntryOriginLabels[source.origin] ??
            JournalEntrySourceTypeLabels[source.type]
        );
    }

    if (source.type === JournalEntrySourceType.Transaction && source.origin) {
        return (
            TransactionOriginLabels[source.origin] ??
            JournalEntrySourceTypeLabels[source.type]
        );
    }

    return JournalEntrySourceTypeLabels[source.type];
}

export function getShortJournalEntryReferenceNumber(referenceNumber: string) {
    return getShortUuidWithPrefix(referenceNumber, "JE");
}

export function getJournalEntrySource(
    journalEntry: Pick<
        JournalEntryNew,
        | "transactionId"
        | "manualJournalEntryId"
        | "openingBalanceId"
        | "loanId"
        | "billId"
        | "invoiceId"
        | "scheduleApplicationId"
    >,
    origin?: ManualJournalEntrySource | TransactionType | null,
): JournalEntrySource {
    if (journalEntry.transactionId) {
        return {
            type: JournalEntrySourceType.Transaction,
            id: journalEntry.transactionId,
            ...(origin && TransactionOriginLabels[origin] ? { origin } : {}),
        };
    }
    if (journalEntry.manualJournalEntryId) {
        return {
            type: JournalEntrySourceType.ManualJournalEntry,
            id: journalEntry.manualJournalEntryId,
            ...(origin ? { origin } : {}),
        };
    }
    if (journalEntry.openingBalanceId) {
        return {
            type: JournalEntrySourceType.OpeningBalance,
            id: journalEntry.openingBalanceId,
        };
    }
    if (journalEntry.loanId) {
        return {
            type: JournalEntrySourceType.Loan,
            id: journalEntry.loanId,
        };
    }

    if (journalEntry.billId) {
        return {
            type: JournalEntrySourceType.Bill,
            id: journalEntry.billId,
        };
    }

    if (journalEntry.invoiceId) {
        return {
            type: JournalEntrySourceType.Invoice,
            id: journalEntry.invoiceId,
        };
    }

    if (journalEntry.scheduleApplicationId) {
        return {
            type: JournalEntrySourceType.Schedule,
            id: journalEntry.scheduleApplicationId,
        };
    }
    throw new Error("Can't determine JE line source");
}

export function displayAccountLabel(
    account: Pick<Account, "codeText" | "name">,
    showAccountNumbers: boolean,
) {
    return showAccountNumbers && account.codeText
        ? `${account.codeText} - ${account.name}`
        : account.name;
}

interface GetSplitLabelParams<T extends Pick<Account, "codeText" | "name">> {
    accountIds: string[];
    accountsById: Map<string, T>;
    showAccountNumbers?: boolean;
}

/**
 * Renders the "Split" column label for a journal entry line: the single
 * offsetting account's label when there is exactly one, "Split" when the line
 * hits multiple offsetting accounts, and empty when the offset is unknown.
 * Shared by the General Ledger and the elimination detail report so both stay
 * consistent.
 */
export function getSplitLabel<T extends Pick<Account, "codeText" | "name">>({
    accountIds,
    accountsById,
    showAccountNumbers = true,
}: GetSplitLabelParams<T>): string {
    if (accountIds.length !== 1) {
        return "Split";
    }

    const account = accountsById.get(accountIds[0]);

    if (account) {
        return displayAccountLabel(account, showAccountNumbers);
    }

    return "";
}

export interface FlatReportSortableAccount {
    codeText: string | null;
    name: string;
    type: string;
}

export function accountSortComparator(
    a: FlatReportSortableAccount,
    b: FlatReportSortableAccount,
    showAccountNumbers: boolean,
): number {
    return flatReportAccountSortComparator(a, b, showAccountNumbers);
}

export function flatReportAccountSortComparator(
    a: FlatReportSortableAccount,
    b: FlatReportSortableAccount,
    showAccountNumbers: boolean,
): number {
    const codeDifference = showAccountNumbers
        ? compareAccountCodes(a.codeText, b.codeText)
        : 0;

    return (
        compareAccountsByLeafGroup(a, b) ||
        codeDifference ||
        a.name.localeCompare(b.name)
    );
}

interface FindFirstSortingLeafAccountParams<
    TNode,
    TLeaf extends FlatReportSortableAccount,
> {
    node: TNode;
    getChildren: (node: TNode) => readonly TNode[] | undefined;
    getLeafAccount: (node: TNode) => TLeaf | undefined;
    showAccountNumbers: boolean;
}

/**
 * Walks a node's descendants and returns the leaf account that sorts first
 * under `flatReportAccountSortComparator`. Group and section rows use this to
 * follow their first child; returns `undefined` for a node with no descendant
 * leaf (an empty group), which callers sort after nodes that have one.
 */
export function findFirstSortingLeafAccount<
    TNode,
    TLeaf extends FlatReportSortableAccount,
>({
    node,
    getChildren,
    getLeafAccount,
    showAccountNumbers,
}: FindFirstSortingLeafAccountParams<TNode, TLeaf>): TLeaf | undefined {
    const leaf = getLeafAccount(node);
    if (leaf) {
        return leaf;
    }
    let anchor: TLeaf | undefined;
    for (const child of getChildren(node) ?? []) {
        const candidate = findFirstSortingLeafAccount({
            node: child,
            getChildren,
            getLeafAccount,
            showAccountNumbers,
        });
        if (
            candidate &&
            (!anchor ||
                flatReportAccountSortComparator(
                    candidate,
                    anchor,
                    showAccountNumbers,
                ) < 0)
        ) {
            anchor = candidate;
        }
    }
    return anchor;
}

type GroupChainNode = Pick<
    AccountGroupHierarchyNode,
    "id" | "name" | "parentGroupId"
>;

export function buildGroupChain<Group extends GroupChainNode>(
    groups: Group[],
    groupId: string | null,
): Group[] {
    if (groupId === null) {
        return [];
    }
    const groupsById = new Map(groups.map((group) => [group.id, group]));
    const chain: Group[] = [];
    const visited = new Set<string>();
    let cursor = groupsById.get(groupId);
    while (cursor && !visited.has(cursor.id)) {
        chain.unshift(cursor);
        visited.add(cursor.id);
        cursor =
            cursor.parentGroupId !== null
                ? groupsById.get(cursor.parentGroupId)
                : undefined;
    }
    return chain;
}

export function buildGroupPathSegments(
    groups: GroupChainNode[],
    groupId: string | null,
): string[] {
    return buildGroupChain(groups, groupId).map((group) => group.name);
}

interface BuildAccountIdentityKeyParams {
    name: string;
    type: AccountType;
    parentGroupId: string | null;
    groups: AccountGroupHierarchyNode[];
}

// Two accounts in different businesses are "the same account" when this key
// matches: name, type and the full group path, case-insensitively.
export function buildAccountIdentityKey({
    name,
    type,
    parentGroupId,
    groups,
}: BuildAccountIdentityKeyParams): string {
    return JSON.stringify([
        name.trim().toLowerCase(),
        type,
        ...buildGroupPathSegments(groups, parentGroupId).map((segment) =>
            segment.trim().toLowerCase(),
        ),
    ]);
}

interface AccountIdentityFields {
    name: string;
    type: AccountType;
    parentGroupId: string | null;
}

interface DidAccountIdentityFieldsChangeParams {
    before: AccountIdentityFields;
    after: Partial<AccountIdentityFields>;
}

// Whether a write touched a field the identity key is built from; an
// undefined field in `after` is one the write left alone. Rule reconciliation
// re-derives the key itself, so reporting a same-key rename is harmless.
export function didAccountIdentityFieldsChange({
    before,
    after,
}: DidAccountIdentityFieldsChangeParams): boolean {
    return (
        (after.name !== undefined && after.name !== before.name) ||
        (after.type !== undefined && after.type !== before.type) ||
        (after.parentGroupId !== undefined &&
            after.parentGroupId !== before.parentGroupId)
    );
}

interface BuildGroupTierLabelsParams {
    type: AccountType;
    groups: AccountGroupHierarchyNode[];
    parentGroupId: string | null;
}

export function buildGroupTierLabels({
    type,
    groups,
    parentGroupId,
}: BuildGroupTierLabelsParams): string[] {
    const leafType = resolveLeafType(type);
    const predefinedTierLabels = leafType
        ? getPredefinedGroupTierLabels(leafType)
        : [getLeafGroupLabel(type)];
    return [
        ...predefinedTierLabels,
        ...buildGroupPathSegments(groups, parentGroupId),
    ];
}

interface GroupHierarchyComparatorParams {
    groups: AccountGroup[];
    showAccountNumbers: boolean;
}

type GroupHierarchySortableAccount = Pick<
    Account,
    "codeText" | "name" | "subtype" | "type" | "parentGroupId"
>;

export function makeGroupHierarchyAccountComparator({
    groups,
    showAccountNumbers,
}: GroupHierarchyComparatorParams): (
    a: GroupHierarchySortableAccount,
    b: GroupHierarchySortableAccount,
) => number {
    const segmentsCache = new Map<string | null, string[]>();
    const segmentsOf = (parentGroupId: string | null): string[] => {
        const cached = segmentsCache.get(parentGroupId);
        if (cached) {
            return cached;
        }
        const segments = buildGroupPathSegments(groups, parentGroupId);
        segmentsCache.set(parentGroupId, segments);
        return segments;
    };
    const comparePathSegments = (a: string[], b: string[]): number => {
        const sharedLength = Math.min(a.length, b.length);
        for (let index = 0; index < sharedLength; index++) {
            const difference = a[index].localeCompare(b[index]);
            if (difference !== 0) {
                return difference;
            }
        }
        return a.length - b.length;
    };
    return (a, b) =>
        compareAccountsByLeafGroup(a, b) ||
        comparePathSegments(
            segmentsOf(a.parentGroupId),
            segmentsOf(b.parentGroupId),
        ) ||
        flatReportAccountSortComparator(a, b, showAccountNumbers);
}

export enum LineItemFallbackAccountType {
    billExpense = "billExpense",
    invoiceRevenue = "invoiceRevenue",
    billAccountsPayable = "billAccountsPayable",
    invoiceAccountsReceivable = "invoiceAccountsReceivable",
    salesTaxPayable = "salesTaxPayable",
    // Contra-revenue fallback for a `kind: "discount"` invoice line item,
    // mirroring the tax fallback above. Used when resolving the default
    // account for auto-generated discount split lines (no user override yet).
    discountsAndAllowances = "discountsAndAllowances",
    // Liability fallback for a `kind: "deposit"` invoice line item, mirroring
    // the tax/discount fallbacks above. Used when resolving the default
    // account for auto-generated deposit split lines (no user override yet).
    customerDeposits = "customerDeposits",
    prepaidExpenses = "prepaidExpenses",
}

const INVOICE_LINE_FALLBACK_ACCOUNT_TYPE_BY_KIND = {
    [InvoiceLineItemKind.ITEM]: LineItemFallbackAccountType.invoiceRevenue,
    [InvoiceLineItemKind.TAX]: LineItemFallbackAccountType.salesTaxPayable,
    [InvoiceLineItemKind.DISCOUNT]:
        LineItemFallbackAccountType.discountsAndAllowances,
    [InvoiceLineItemKind.DEPOSIT]: LineItemFallbackAccountType.customerDeposits,
} satisfies Record<InvoiceLineItemKind, LineItemFallbackAccountType>;

export function getInvoiceLineFallbackAccountType(
    kind: InvoiceLineItemKind,
): LineItemFallbackAccountType {
    return INVOICE_LINE_FALLBACK_ACCOUNT_TYPE_BY_KIND[kind];
}

interface GetFallbackAccountRoleForLineItemParams {
    lineItemFallbackAccountType: LineItemFallbackAccountType;
}

export function getFallbackAccountRoleForLineItem({
    lineItemFallbackAccountType,
}: GetFallbackAccountRoleForLineItemParams) {
    switch (lineItemFallbackAccountType) {
        case LineItemFallbackAccountType.billExpense:
            return AccountRole.uncategorizedExpense;
        case LineItemFallbackAccountType.invoiceRevenue:
            return AccountRole.defaultRevenue;
        case LineItemFallbackAccountType.billAccountsPayable:
            return AccountRole.accountsPayable;
        case LineItemFallbackAccountType.invoiceAccountsReceivable:
            return AccountRole.accountsReceivable;
        case LineItemFallbackAccountType.salesTaxPayable:
            return AccountRole.salesTaxPayable;
        case LineItemFallbackAccountType.discountsAndAllowances:
            return AccountRole.discountsAndAllowances;
        case LineItemFallbackAccountType.customerDeposits:
            return AccountRole.customerDeposits;
        case LineItemFallbackAccountType.prepaidExpenses:
            return AccountRole.prepaidExpenses;
        default:
            throw new Error(
                `Unknown fallback account type: ${lineItemFallbackAccountType}`,
            );
    }
}

// Human-readable label for a fallback account type, used in error messages
// (e.g. "Fallback expense account not found ..."). Kept distinct from the raw
// enum value so messages stay readable instead of leaking "billExpense" etc.
export function getFallbackAccountLabelForLineItem({
    lineItemFallbackAccountType,
}: GetFallbackAccountRoleForLineItemParams): string {
    switch (lineItemFallbackAccountType) {
        case LineItemFallbackAccountType.billExpense:
            return "expense";
        case LineItemFallbackAccountType.invoiceRevenue:
            return "revenue";
        case LineItemFallbackAccountType.billAccountsPayable:
            return "accounts payable";
        case LineItemFallbackAccountType.invoiceAccountsReceivable:
            return "accounts receivable";
        case LineItemFallbackAccountType.salesTaxPayable:
            return "sales tax payable";
        case LineItemFallbackAccountType.discountsAndAllowances:
            return "discounts and allowances";
        case LineItemFallbackAccountType.customerDeposits:
            return "customer deposits";
        case LineItemFallbackAccountType.prepaidExpenses:
            return "prepaid expenses";
        default:
            throw new Error(
                `Unknown fallback account type: ${lineItemFallbackAccountType}`,
            );
    }
}

const accountCodeCollator = new Intl.Collator("en", { numeric: true });

/**
 * Uses numeric collation with raw-string tie-breaking for distinct,
 * equal-magnitude codes such as `"01"` and `"1"`. Null sorts last.
 */
export function compareAccountCodes(
    a: string | null,
    b: string | null,
): number {
    if (a === null || b === null) {
        if (a === b) {
            return 0;
        }
        return a === null ? 1 : -1;
    }

    const collated = accountCodeCollator.compare(a, b);
    if (collated !== 0) {
        return collated;
    }

    if (a === b) {
        return 0;
    }

    return a < b ? -1 : 1;
}

// Only a run of digits: `Number.parseInt` would otherwise truncate decimals/
// signs and swallow trailing characters (e.g. "1.5" -> 1, "12abc" -> 12).
export function parsePlainIntegerCodeText(
    codeText: string | null,
): number | null {
    if (codeText === null) {
        return null;
    }
    const trimmed = codeText.trim();
    if (!/^\d+$/.test(trimmed)) {
        return null;
    }
    const parsed = Number.parseInt(trimmed, 10);
    return Number.isNaN(parsed) ? null : parsed;
}

// Temporary compatibility conversion, removed in PRO-10774 once `code` is text.
export function codeToText(code: number): string;
export function codeToText(code: number | null): string | null;
export function codeToText(code: number | null): string | null {
    return code === null ? null : String(code);
}

export function preferCodeText(
    codeText: string | null | undefined,
    code: number | null,
): string | null {
    return codeText === undefined ? codeToText(code) : codeText;
}

const soleProp = defaultSolePropEquityChartOfAccounts;
const sCorp = defaultSCorpEquityChartOfAccounts;
const partnership = defaultPartnershipEquityChartOfAccounts;

/**
 * Standard Kick CoA accounts that are equivalent across entity types.
 * When changing entity type, these accounts represent the same economic concept:
 * - Capital (Partnership) / Retained Earnings (Corporation) / Owner Capital (Sole Prop)
 * - Distributions (Partnership/Corporation) / Owner Draws (Sole Prop)
 * - Contributions (Partnership/Corporation) / Owner Investments (Sole Prop)
 */
const EQUIVALENT_ACCOUNTS: ReadonlyArray<readonly string[]> = [
    [
        partnership[PartnershipAccountCodes.Capital].name as string,
        sCorp[SCorpAccountCodes.RetainedEarnings]!.name as string,
        soleProp[SolePropEquityAccountCodes.OwnerCapital].name as string,
    ],
    [
        partnership[PartnershipAccountCodes.Distributions].name as string,
        sCorp[SCorpAccountCodes.Distributions]!.name as string,
        soleProp[SolePropEquityAccountCodes.OwnerDraws].name as string,
    ],
    [
        partnership[PartnershipAccountCodes.Contributions].name as string,
        sCorp[SCorpAccountCodes.Contributions]!.name as string,
        soleProp[SolePropEquityAccountCodes.OwnerInvestments].name as string,
    ],
];

function findEquivalentNames(name: string): readonly string[] | undefined {
    return EQUIVALENT_ACCOUNTS.find((group) => group.includes(name));
}

/**
 * Find a matching account in a target ledger using tiered matching:
 * 1. Exact match by name, type, and subtype
 * 2. Match by equivalent account name (handles cross-entity-type mapping,
 *    e.g. "Owner Capital" <-> "Retained Earnings" <-> "Capital")
 */
export function getMatchingAccount<
    T extends Pick<Account, "name" | "type" | "subtype">,
>(
    accounts: T[],
    target: Pick<Account, "name" | "type" | "subtype">,
): T | undefined {
    const exactMatch = accounts.find(
        (a) =>
            a.name === target.name &&
            a.type === target.type &&
            a.subtype === target.subtype,
    );
    if (exactMatch) {
        return exactMatch;
    }

    const equivalentNames = findEquivalentNames(target.name);
    if (!equivalentNames) {
        return undefined;
    }

    return accounts.find(
        (a) => a.type === target.type && equivalentNames.includes(a.name),
    );
}

export type AccountCodeMatchTarget = Pick<
    Account,
    "name" | "type" | "subtype" | "code" | "kickOriginalCode"
>;

/**
 * Tiered account matching that puts code identity above name identity:
 * 1. Same `kickOriginalCode` (native Kick CoA accounts keep their original
 *    code across entities even when renamed)
 * 2. Same `code` (codes are user-managed identity on a custom chart)
 * 3. The name/type/subtype tiers of {@link getMatchingAccount}
 */
export function getMatchingAccountWithCodes<T extends AccountCodeMatchTarget>(
    accounts: T[],
    target: AccountCodeMatchTarget,
): T | undefined {
    if (target.kickOriginalCode !== null) {
        const kickCodeMatch = accounts.find(
            (a) => a.kickOriginalCode === target.kickOriginalCode,
        );
        if (kickCodeMatch) {
            return kickCodeMatch;
        }
    }
    if (target.code !== null) {
        const codeMatch = accounts.find((a) => a.code === target.code);
        if (codeMatch) {
            return codeMatch;
        }
    }
    return getMatchingAccount(accounts, target);
}
